import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	FormControl,
	Grid,
	MenuItem,
	Select,
	Typography,
	SelectChangeEvent,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import TextFieldComponent from 'common/components/TextField/TextField';
import {
	CONTROL_TYPE,
	CONTROL_FREQUENCY,
	CONTROL_STATEMENT_DEFAULT_OPTIONS,
} from 'common/constants/AISimulationConstants';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationControlOwnerValue,
	setSimulationControlTypeValue,
	setSimulationControlFrequencyValue,
} from 'common/store/reducer/simulationReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { IControlStatementDropdown } from '../api/type-def';

import styles from './ControlStatementDropdown.module.scss';

const ControlStatementDropdown = (props: IControlStatementDropdown) => {
	const { items } = props;

	const dispatch = useDispatch();

	const controlOwnerValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlOwnerValue,
	);
	const controlTypeValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlTypeValue,
	);
	const controlFrequencyValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlFrequencyValue,
	);

	const [owner, setOwner] = useState(controlOwnerValue);
	const [selectedControlType, setSelectedControlType] =
		useState(controlTypeValue);
	const [selectedControlFrequency, setSelectedControlFrequency] = useState(
		controlFrequencyValue,
	);

	const onChangeValue = (event: any) => {
		setOwner(event.target.value);
		dispatch(setSimulationControlOwnerValue(event.target.value.trim()));
	};

	const onBlur = (event: any) => {
		setOwner(event.target.value.trim());
	};

	const handleSelectedControlType = (e: SelectChangeEvent<string>) => {
		setSelectedControlType(e.target.value);
		dispatch(setSimulationControlTypeValue(e.target.value));
	};

	const handleSelectedControlFrequency = (e: SelectChangeEvent<string>) => {
		setSelectedControlFrequency(e.target.value);
		dispatch(setSimulationControlFrequencyValue(e.target.value));
	};

	useEffect(() => {
		if (items) {
			if (items.controlOwner) {
				setOwner(items.controlOwner);
				dispatch(setSimulationControlOwnerValue(items.controlOwner));
			}
			if (items.controlType) {
				setSelectedControlType(items.controlType);
				dispatch(setSimulationControlTypeValue(items.controlType));
			}
			if (items.controlFrequency) {
				setSelectedControlFrequency(items.controlFrequency);
				dispatch(setSimulationControlFrequencyValue(items.controlFrequency));
			}
		} else {
			dispatch(setSimulationControlOwnerValue(''));
			dispatch(
				setSimulationControlTypeValue(CONTROL_STATEMENT_DEFAULT_OPTIONS),
			);
			dispatch(
				setSimulationControlFrequencyValue(CONTROL_STATEMENT_DEFAULT_OPTIONS),
			);
			setOwner('');
			setSelectedControlType(CONTROL_STATEMENT_DEFAULT_OPTIONS);
			setSelectedControlFrequency(CONTROL_STATEMENT_DEFAULT_OPTIONS);
		}
	}, []);

	return (
		<Grid container>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<FormControl fullWidth>
					<div>
						<Typography className={styles['label-style']}>
							{toSentenceCase('Control Type')}
						</Typography>
						<Select
							size="small"
							sx={{ background: 'white' }}
							labelId="demo-simple-select-label"
							id="demo-simple-select"
							defaultValue="Select an option"
							onChange={handleSelectedControlType}
							IconComponent={ExpandMoreRoundedIcon}
							inputProps={{
								'data-testid': 'control-type-dropdown-select',
							}}
							className={styles['dropdown-width']}
							value={selectedControlType}
						>
							<MenuItem value="Select an option">Select an option</MenuItem>
							{CONTROL_TYPE.map((option) => (
								<MenuItem
									data-testid="selected-type-list-item"
									key={option.value}
									value={option.value}
								>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</div>
				</FormControl>
			</Grid>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<FormControl fullWidth>
					<div>
						<Typography className={styles['label-style']}>
							{toSentenceCase('Control Owner')}
						</Typography>
						<TextFieldComponent
							fullWidth
							inputProps={{
								'data-testid': 'owner-name',
							}}
							type="text"
							name="Assign an Owner"
							value={owner}
							onChange={(e) => onChangeValue(e)}
							onBlur={onBlur}
							className={styles['dropdown-width']}
							placeholder="Assign an Owner"
						/>
					</div>
				</FormControl>
			</Grid>
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<FormControl fullWidth>
					<div>
						<Typography className={styles['label-style']}>
							{toSentenceCase('Control Frequency')}
						</Typography>
						<Select
							size="small"
							sx={{ background: 'white' }}
							labelId="demo-simple-select-label"
							id="demo-simple-select"
							defaultValue="Select an option"
							IconComponent={ExpandMoreRoundedIcon}
							onChange={handleSelectedControlFrequency}
							inputProps={{
								'data-testid': 'control-frequency-dropdown-select',
							}}
							className={styles['dropdown-width']}
							value={selectedControlFrequency}
						>
							<MenuItem value="Select an option">Select an option</MenuItem>
							{CONTROL_FREQUENCY.map((option) => (
								<MenuItem
									data-testid="selected-frequency-list-item"
									key={option.value}
									value={option.value}
								>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</div>
				</FormControl>
			</Grid>
		</Grid>
	);
};

export default ControlStatementDropdown;
